/**
 * Project Tracker Main Component
 *
 * Main entry point for the Project Contribution Tracker feature.
 * Handles routing between different views based on user role.
 */

import React, { useState } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import {
  Box,
  Tabs,
  Tab,
  Button,
} from '@mui/material';
import {
  Work as ProjectsIcon,
  Search as SearchIcon,
  Create as CreateIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import ProjectList from './components/ProjectList';
import ProjectDetail from './components/ProjectDetail';
import MyProjects from './components/MyProjects';
import ExperienceSearch from './components/ExperienceSearch';
import CreateProjectDialog from './components/CreateProjectDialog';
import AdminPanel from './components/admin/AdminPanel';
import { useProjects } from './hooks/useProjects';

const ProjectTracker: React.FC = () => {
  const navigate = useNavigate();
  const { createProject } = useProjects();
  const { hasAnyGroup } = useAuth();

  // Check if user is admin (platform admin group).
  // AUTHCFG-REVIEW-002 (Round 7 sweep #134, finding 904cc830): match
  // the fail-closed pattern in frontend/src/config/features.ts and
  // AdminPermissionSwitch.tsx. When the env var is unset, fall back
  // to no group (admin gating fails closed) instead of a tenant-
  // specific GUID that would silently grant or deny incorrectly in
  // any other deployment.
  const adminGroupId = process.env.REACT_APP_PROFILE_ADMIN_GROUP_ID || '';
  const isAdmin = hasAnyGroup(adminGroupId ? [adminGroupId] : []);

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);

  // Determine which tab is active based on current path
  React.useEffect(() => {
    const path = window.location.pathname;
    if (path.includes('/projects/admin')) {
      setCurrentTab(-1); // No tab selected for admin view
    } else if (path.includes('/projects/my-projects')) {
      setCurrentTab(1);
    } else if (path.includes('/projects/find-expertise')) {
      setCurrentTab(2);
    } else {
      setCurrentTab(0);
    }
  }, []);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
    switch (newValue) {
      case 0:
        navigate('/projects');
        break;
      case 1:
        navigate('/projects/my-projects');
        break;
      case 2:
        navigate('/projects/find-expertise');
        break;
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Navigation Tabs */}
      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Tabs value={currentTab} onChange={handleTabChange} sx={{ flexGrow: 1 }}>
          <Tab icon={<CreateIcon />} label="My Projects" iconPosition="start" />
          <Tab icon={<ProjectsIcon />} label="Assigned Projects" iconPosition="start" />
          <Tab icon={<SearchIcon />} label="Find Expertise" iconPosition="start" />
        </Tabs>
        {isAdmin && (
          <Button
            startIcon={<SettingsIcon />}
            onClick={() => navigate('/projects/admin')}
            sx={{ mr: 2 }}
          >
            Admin
          </Button>
        )}
      </Box>

      {/* Routes */}
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        <Routes>
          {/* Project Creator Routes */}
          <Route index element={<ProjectList />} />
          <Route
            path="create"
            element={
              <>
                <ProjectList key="create-route" />
                <CreateProjectDialog
                  open={true}
                  onClose={() => navigate('/projects')}
                  onSubmit={async (request) => {
                    await createProject(request);
                    // Add timestamp to force route change and component remount
                    navigate('/projects?refresh=' + Date.now());
                  }}
                />
              </>
            }
          />
          <Route path=":projectId" element={<ProjectDetail />} />

          {/* Team Member Routes (Read-Only) */}
          <Route path="my-projects" element={<MyProjects />} />
          <Route path="find-expertise" element={<ExperienceSearch />} />

          {/* Admin Routes */}
          {isAdmin && <Route path="admin" element={<AdminPanel />} />}

          {/* Default redirect */}
          <Route path="*" element={<Navigate to="/projects" replace />} />
        </Routes>
      </Box>

      {/* Create Project Dialog */}
      <CreateProjectDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSubmit={async (request) => {
          await createProject(request);
        }}
      />
    </Box>
  );
};

export default ProjectTracker;
